<template>
  <b-modal
    header-bg-variant="primary"
    id="modal-template"
    size="xl"
    centered
    scrollable
    static
    no-close-on-esc
    no-close-on-backdrop
    :title="$t('maintenance.modal.template.title')"
    @hidden="resetModal"
  >
    <McAlert class="mb-3" variant="info" :text="$t('maintenance.tooltip.template')" withIcon />
    <b-row>
      <b-col md="4">
        <div>
          <h4>{{ $t('maintenance.modal.template.list') }}</h4>
          <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
            <template #prepend>
              <i class="ps-2 mc-icon icon-fill_search"></i>
            </template>
            <b-form-input
              :placeholder="$t('global.search') + '...'"
              :value="search"
              @input="handleSearchChange"
              ref="searchInput"
            ></b-form-input>
            <template #append v-if="search">
              <button class="clear-search-btn" @click="handleClearSearch">
                <i class="pe-2 mc-icon icon-outline_close"></i>
              </button>
            </template>
          </b-input-group>
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <div class="m-4">
                <b-skeleton width="85%"></b-skeleton>
                <b-skeleton width="55%"></b-skeleton>
                <b-skeleton width="70%"></b-skeleton>
              </div>
            </template>
            <div class="service-template-list">
              <ul class="list-unstyled" v-if="items.length > 0">
                <li v-for="item in items" :key="item.templateId" @click="handleTemplate(item)">
                  <div class="list-item" :class="{ active: selectedId == item.templateId }">
                    <img
                      :src="require(`@/assets/images/vehicles/${item.icon}/${item.icon}-blue.png`)"
                      :alt="item.name"
                    />
                    <span>{{ item.name }}</span>
                  </div>
                </li>
              </ul>
              <div class="d-flex justify-content-center m-4" v-else>
                <h6>{{ 'Tidak ada data' }}</h6>
              </div>
            </div>
          </b-skeleton-wrapper>
        </div>
      </b-col>
      <b-col md="8" class="d-flex align-items-center justify-content-center" v-if="!selectedId">
        <McEmptyState
          :title="$t('maintenance.nolist.template')"
          :desc="$t('maintenance.nolist.templateDesc')"
          :hasButton="false"
          customFigure="search"
        ></McEmptyState>
      </b-col>
      <b-col md="8" v-else>
        <b-skeleton-wrapper :loading="loadDetail">
          <template #loading>
            <b-skeleton width="85%"></b-skeleton>
            <b-skeleton width="55%"></b-skeleton>
            <b-skeleton width="70%"></b-skeleton>
          </template>
          <div class="d-flex justify-content-between">
            <h4>{{ `${$t('maintenance.modal.template.preview')} ~ ${vehicleType}` }}</h4>
            <span class="teal-300">{{ `${totalServices} ${$t('global.scheduled')}` }}</span>
          </div>
          <div class="template-table my-3 border border-1">
            <b-table :fields="fields" :items="serviceItems" show-empty :busy="loadDetail">
              <template #empty>
                <div v-show="!loadDetail" class="text-center my-4">
                  {{ $t('global.text.noData') }}
                </div>
              </template>
              <template #cell(compService)="data">
                {{ `${data.item.service.name} (${data.item.service.serviceAction.action})` }}
              </template>
              <template #cell(catService)="data">
                {{ data.item.service.serviceCategory.name }}
              </template>
              <template #cell(interval)="data">
                {{
                  data.item.type == 'DistanceAndTime'
                    ? `${localeThousandFormat(data.item.intervalDistance)} Km & ${
                        data.item.intervalTime
                      } ${$t(`global.time.${data.item.intervalPeriod}`)}`
                    : data.item.intervalHourmeter
                }}
              </template>
            </b-table>
          </div>
        </b-skeleton-wrapper>
      </b-col>
    </b-row>
    <template #modal-footer="{ cancel }">
      <div class="d-flex float-end">
        <McButton
          variant="secondary"
          class="me-3"
          icon="icon-outline_close"
          :text="$t('global.cancel')"
          @click="cancel()"
        />
        <McButton
          icon="icon-outline_checkmark"
          :text="$t('global.form.select')"
          @click="handleSubmit()"
        />
      </div>
    </template>
  </b-modal>
</template>
<script>
import McAlert from '@/components/McAlert'
import McButton from '@/components/McButton'
import McEmptyState from '@/components/McEmptyState'
import {
  GET_SERVICE_TEMPLATES,
  GET_SERVICE_TEMPLATES_BY_ID,
} from '@/services/api/maintenance/ssgroup'
import { Helper } from '@/mixin/helper'
import { debounce } from '@/helpers/debounce'

export default {
  components: {
    McAlert,
    McButton,
    McEmptyState,
  },
  mixins: [Helper],
  data() {
    return {
      modalShown: false,
      search: '',
      selectedId: null,
      vehicleType: '',
      loading: false,
      loadDetail: false,
      totalServices: 0,
      items: [],
      serviceItems: [],
    }
  },
  computed: {
    fields() {
      return [
        {
          key: 'compService',
          label: this.$t('maintenance.field.compName'),
        },
        {
          key: 'catService',
          label: this.$t('maintenance.form.catService'),
        },
        {
          key: 'interval',
          label: this.$t('report.object.interval'),
        },
      ]
    },
  },
  async mounted() {
    await this.getData()
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    async getData() {
      this.loading = true
      const res = await this.$apollo.query({
        query: GET_SERVICE_TEMPLATES,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            search: this.search,
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        },
      })

      this.items = res.data.serviceTemplates.serviceTemplates.data
      setTimeout(() => {
        this.loading = false
      }, 300)
    },
    resetModal() {
      this.handleSearchChange(undefined)
      this.selectedId = null
      this.serviceItems = []
      this.totalServices = 0
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      await this.getData()
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    async handleTemplate(data) {
      this.loadDetail = true
      this.selectedId = data.templateId
      this.vehicleType = data.name
      const res = await this.$apollo.query({
        query: GET_SERVICE_TEMPLATES_BY_ID,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            templateId: this.selectedId,
          },
        },
      })
      this.serviceItems = res.data.serviceTemplate.serviceTemplate.serviceTemplateSchedule
      this.totalServices = res.data.serviceTemplate.serviceTemplate.totalServices

      setTimeout(() => {
        this.loadDetail = false
      }, 200)
    },
    async handleSubmit() {
      this.$emit('handleSubmitTemplate', this.selectedId)
    },
  },
}
</script>
<style lang="scss" scoped>
.mc-has-search {
  border-width: $input-border-width;
  border-color: $input-border-color;
  border-radius: 4px;
  border-style: solid;

  border-radius: 4px;

  // if search is disabled
  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    height: 37px;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon:before {
      font-size: 16px;
      color: $input-placeholder-color;
    }
  }
}
.service-template-list {
  height: 400px;
  margin-top: 1rem;
  border: 1px solid $teal-200;
  border-radius: 4px;
  overflow: auto;

  .list-item {
    display: flex;
    align-items: center;
    padding-left: 1.5rem;
    border-bottom: 1px solid $teal-100;
    cursor: pointer;

    &:hover {
      background-color: $teal-100;
      position: relative;
    }

    &.active {
      background-color: $teal-100;
      position: relative;

      &:before {
        content: '✓';
        position: absolute;
        right: 10px;
        top: 20px;
        color: $white;
        background: $green-400;
        border-radius: 100%;
        padding: 0 4px;
        font-size: 10px;
      }
    }

    img {
      transform: rotate(270deg);
    }

    span {
      font-weight: 600;
      margin-left: 2rem;
    }
  }
}
.template-table {
  max-height: 456px;
  overflow-y: auto;
}
</style>
